/**
 * Column definitions for the Jobs list table.
 * Mirrors legacy columns: ID, Title, Location, Status, Public, Locale, Type, Level,
 * Country, Salary + a row-actions column (edit / toggle public / delete).
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import type { StatusTone } from '@vnetwork/ui/data';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  IconButton,
} from '@vnetwork/ui/primitives';
import { Eye, EyeOff, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { Job } from '@/api/jobs';

export type JobCol = ColumnDef<Job, unknown>;

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

export function jobStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'Opened': return 'success';
    case 'Closed': return 'danger';
    default: return 'neutral';
  }
}

export function publicTone(pub?: boolean): StatusTone {
  return pub ? 'success' : 'muted';
}

export interface JobColumnCallbacks {
  onEdit: (row: Job) => void;
  onTogglePublic: (row: Job) => void;
  onDelete: (row: Job) => void;
  publicPending: boolean;
}

export function buildJobColumns(cbs: JobColumnCallbacks): JobCol[] {
  return [
    {
      id: 'job_id',
      header: 'ID',
      cell: ({ row }) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {row.original.job_id}
        </span>
      ),
    },
    {
      id: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <span
          title={row.original.title}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 240,
            fontWeight: 'var(--vn-font-weight-medium)',
          }}
        >
          {row.original.title}
        </span>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <span
          title={row.original.location}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 180,
          }}
        >
          {row.original.location || <span style={muted}>—</span>}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={jobStatusTone(row.original.status)}>
          {row.original.status}
        </StatusPill>
      ),
    },
    {
      id: 'public',
      header: 'Public',
      cell: ({ row }) => (
        <StatusPill tone={publicTone(row.original.public)}>
          {row.original.public ? 'Public' : 'Private'}
        </StatusPill>
      ),
    },
    {
      id: 'locale',
      header: 'Locale',
      cell: ({ row }) => (
        <span style={{ whiteSpace: 'nowrap' }}>{row.original.locale}</span>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => row.original.type || <span style={muted}>—</span>,
    },
    {
      id: 'level',
      header: 'Level',
      cell: ({ row }) => (
        <span
          title={row.original.level}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 180,
          }}
        >
          {row.original.level || <span style={muted}>—</span>}
        </span>
      ),
    },
    {
      id: 'country',
      header: 'Country',
      cell: ({ row }) => row.original.country || <span style={muted}>—</span>,
    },
    {
      id: 'salary',
      header: 'Salary',
      cell: ({ row }) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {row.original.salary || <span style={muted}>—</span>}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton size="sm" variant="ghost" aria-label="Row actions">
                <MoreHorizontal size={15} />
              </IconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => cbs.onEdit(job)}>
                <Pencil size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => cbs.onTogglePublic(job)}
                disabled={cbs.publicPending}
              >
                {job.public ? (
                  <EyeOff size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                ) : (
                  <Eye size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                )}
                {job.public ? 'Unpublish' : 'Publish'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem tone="danger" onSelect={() => cbs.onDelete(job)}>
                <Trash2 size={14} style={{ marginRight: 'var(--vn-space-2)' }} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}
